import React, {FC, SyntheticEvent, useCallback, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ToolbarButton} from '../../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {SimpleDropdownWithHeader} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownWithHeader';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {IntegrationFileAttachment} from '../../../../../helpers/integrationHelpers';
import {IntegrationModel} from '../../../../../models/api/integrationModel';
import {AttachmentFileUploader} from '../../../../common/attachments/attachmentsTypes';
import {useIntegrationAttachments} from './attachmentsHooks/attachmentsHook';

/*
 * Props.
 */

export interface AttachmentsToolbarButtonPublicProps {
  onIntegrationAttachLinks: HandlerOf<ReadonlyArray<IntegrationFileAttachment>>;
  onFileUpload: AttachmentFileUploader;
}

interface AttachmentsToolbarButtonProps extends AttachmentsToolbarButtonPublicProps {
  integrations: ReadonlyArray<IntegrationModel>;
}

/*
 * Style.
 */

const StyledFileLabel = styled.label`
  display: block;

  input {
    display: none;
  }
`;

/*
 * Component.
 */

export const AttachmentsToolbarButton: FC<AttachmentsToolbarButtonProps> = (props) => {
  const {integrations, onFileUpload, onIntegrationAttachLinks} = props;
  const hasIntegrations = integrations.length > 0;

  const [inputKey, setInputKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /*
   * Event handlers.
   */

  const onFileInputChange = useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      // For each file in the input, add it.
      const {files} = event.currentTarget;
      const fileList = files && Array.from(files);
      if (fileList && fileList.length > 0) {
        fileList.forEach((f) => onFileUpload(f));
      }

      // Re-create the input to avoid existing-value issues.
      setInputKey((value) => value + 1);
    },
    [onFileUpload, setInputKey],
  );

  const onSimpleAttachClick = useCallback(() => {
    if (!fileInputRef.current) {
      return;
    }

    fileInputRef.current.click();
  }, []);

  const {showIntegrationAttachmentsPicker} = useIntegrationAttachments(onIntegrationAttachLinks);
  const onIntegrationAttachClick = showIntegrationAttachmentsPicker;

  /*
   * Render.
   */

  const renderInputFilePicker = () => (
    <StyledFileLabel>
      <input ref={fileInputRef} key={inputKey} type="file" onChange={onFileInputChange} multiple />
    </StyledFileLabel>
  );

  return (
    <>
      {renderInputFilePicker()}
      {!hasIntegrations && renderSimpleAttachmentButton(onSimpleAttachClick)}
      {hasIntegrations && (
        <AttachDropdownCoordinator
          integrations={integrations}
          onSimpleAttachClick={onSimpleAttachClick}
          onIntegrationAttachClick={onIntegrationAttachClick}
        />
      )}
    </>
  );
};

/*
 * Simple button.
 */

function renderSimpleAttachmentButton(onSimpleAttachClick: Handler) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <ToolbarButton
          onClick={onSimpleAttachClick}
          iconName="attachment"
          label={<Trans id="composer-attachments">Attach files</Trans>}
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
        />
      )}
    </UserThemeConsumer>
  );
}

/*
 * Dropdown.
 */

interface AttachDropdownCoordinatorProps {
  integrations: ReadonlyArray<IntegrationModel>;
  onSimpleAttachClick: Handler;
  onIntegrationAttachClick: HandlerOf<IntegrationModel>;
}

const AttachDropdownCoordinator: FC<AttachDropdownCoordinatorProps> = (props) => (
  <PopoverCoordinator
    renderButton={(isOpen) => renderDropdownButton(isOpen)}
    renderModal={(modalProps) => renderDropdown(modalProps, props)}
  />
);

function renderDropdownButton(isOpen: boolean) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <ToolbarButton
          hasHover={isOpen}
          iconName="attachment"
          label={<Trans id="composer-attachments">Attach files</Trans>}
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
        />
      )}
    </UserThemeConsumer>
  );
}

function renderDropdown(modalProps: PopoverTransferProps, props: AttachDropdownCoordinatorProps) {
  return (
    <SimpleDropdownWithHeader {...modalProps} title={<Trans id="attach-dropdown-header">Attachments</Trans>}>
      {renderComputerItem(props.onSimpleAttachClick)}
      {renderIntegrations(props)}
    </SimpleDropdownWithHeader>
  );
}

function renderComputerItem(onSimpleAttachClick: Handler) {
  return (
    <SimpleDropdownItem key="attach-from-computer" onSelect={onSimpleAttachClick}>
      <Trans id="attach-from-computer">Attach from computer</Trans>
    </SimpleDropdownItem>
  );
}

function renderIntegrations(props: AttachDropdownCoordinatorProps) {
  return props.integrations.map((i) => (
    <SimpleDropdownItem key={i.id} onSelect={() => props.onIntegrationAttachClick(i)}>
      <Trans id="attach-from-integration">Attach from {i.name}</Trans>
    </SimpleDropdownItem>
  ));
}
